import React, {Fragment, useEffect, useState, useContext} from 'react';
import {Tabs, Breadcrumb, Tooltip, Row, Col, Tag, Badge, Space, Descriptions, Form, Select, Input, Divider, Typography, Checkbox} from 'antd';
import {useRouteMatch, Link, useHistory} from 'react-router-dom';
import {extract} from '../../utils/openapi-extractor';
import {EyeOutlined, CodeOutlined, MacCommandOutlined} from '@ant-design/icons';
import {TechnicalEditor} from '../technical-editor';
import {Block} from '../block';
import {OpenApiContext} from '../../openapi-context';
import {AppContext} from '../../state';
import {LintView} from '../lint-view';
import {errorsForModel} from '../../utils/openapi-schema-validator';
import {MarkdownView} from '../markdown-block';
import {MarkdownEditor} from '../markdown-editor';
import {cloneDeep, set, get, unset} from 'lodash';
import {camelCase, snakeCase} from 'change-case';
import {useLocation} from 'react-use';

function ComponentName({model}) {
    return (
        <h2>
            <Space>
                <span>{model.spec.title || model.name}</span>
                <Tag color="blue">param</Tag>
            </Space>
        </h2>
    );
}

function ParameterEditor({parameter, setIsValid}) {
    const [form] = Form.useForm();

    const changeIn = (val) => (parameter.spec.in = val);
    const changeName = (e) => {
        parameter.spec.name = e.target.value;
        setIsValid(parameter.spec.name.length > 0 && !!parameter.spec.name.match(/^[\D]{1}[\w]+/));
    };
    const changeDeprecated = (e) => {
        parameter.spec.deprecated = e.target.checked;
    };
    const changeRequired = (e) => {
        parameter.spec.required = e.target.checked;
    };
    const changeType = (newType) => {
        set(parameter.spec, 'schema.type', newType);
        setIsValid(!!newType);
    };

    const changeAllowEmptyValue = (e) => {
        parameter.spec.allowEmptyValue = e.target.checked;
    };
    const changeDescription = (val) => (parameter.spec.description = val);

    useEffect(() => {
        async function validateFields() {
            try {
                await form.validateFields();
                setIsValid(true);
            } catch (err) {
                setIsValid(false);
            }
        }

        if (parameter) {
            form.setFieldsValue({type: get(parameter, 'spec.schema.type')});
        }
        validateFields();
    }, [parameter]);

    return parameter ? (
        <Fragment>
            <Typography.Title level={4}>Description</Typography.Title>
            <MarkdownEditor value={parameter.spec.description} onChange={changeDescription} height={175} />
            <Divider />
            <Form form={form} initialValues={parameter.spec}>
                <Form.Item
                    label="Name"
                    name="name"
                    normalize={camelCase}
                    onChange={changeName}
                    rules={[
                        {required: true, message: 'Name is required'},
                        {pattern: /^[\D]{1}[\w]+/, message: 'Name must be a valid identifier'},
                    ]}>
                    <Input />
                </Form.Item>
                <Form.Item label="Type" name="type" rules={[{required: true, message: 'Type is required'}]}>
                    <Select style={{width: '100%'}} onChange={changeType}>
                        <Select.Option value="string">string</Select.Option>
                        <Select.Option value="number">number</Select.Option>
                        <Select.Option value="boolean">boolean</Select.Option>
                        <Select.Option value="object">object</Select.Option>
                        <Select.Option value="array">array</Select.Option>
                    </Select>
                </Form.Item>
                <Form.Item label="In" name="in" rules={[{required: true, message: 'In is required'}]}>
                    <Select style={{width: '100%'}} onChange={changeIn}>
                        <Select.Option value="path">path</Select.Option>
                        <Select.Option value="query">query</Select.Option>
                        <Select.Option value="header">header</Select.Option>
                        <Select.Option value="cookie">cookie</Select.Option>
                    </Select>
                </Form.Item>
                <Form.Item label="Deprecated?" name="deprecated" valuePropName="checked">
                    <Checkbox onChange={changeDeprecated} />
                </Form.Item>
                <Form.Item label="Required?" name="required" valuePropName="checked">
                    <Checkbox onChange={changeRequired} />
                </Form.Item>
                <Form.Item label="Allow Empty Value?" name="allowEmptyValue" valuePropName="checked">
                    <Checkbox onChange={changeAllowEmptyValue} />
                </Form.Item>
            </Form>
        </Fragment>
    ) : null;
}
export const ParameterView = function ({spec, dispatch, mobile}) {
    const app = useContext(AppContext);
    const {state, SUPPORTED_EDITOR_ROLES} = useContext(OpenApiContext);
    const {params} = useRouteMatch('/:env/standards/:category/:key/:version/parameters/:modelKey');
    const history = useHistory();
    const location = useLocation();
    const [model, updateModel] = useState();
    const [errorCount, setErrorCount] = useState(0);
    const [editMode, setEditMode] = useState();
    const [editedParameter, setEditedParameter] = useState();
    const [isValid, setIsValid] = useState();

    useEffect(() => {
        updateModel(extract(spec).parameter(params.modelKey));
        app.dispatch({
            type: 'add-breadcrumb-entry',
            entry: {
                env: params.env,
                linkType: 'parameters',
                category: params.category,
                key: params.key,
                version: params.version,
                modelKey: params.modelKey,
            },
        });
    }, [spec, params.modelKey]);

    useEffect(() => {
        if (model) {
            setErrorCount(errorsForModel(state.specValidationErrors, model.name).length);
        } else {
            setErrorCount(0);
        }
    }, [spec, params.modelKey, model, state.specValidationErrors]);

    function onTechnicalEdit(newModel) {
        dispatch({
            type: 'update-parameter-def',
            parameter: {...editedParameter, spec: newModel},
        });
    }

    const canEdit = () => {
        return SUPPORTED_EDITOR_ROLES.indexOf(state.role) !== -1;
    };

    function triggerEdit(editing) {
        setEditMode(editing);
        if (editing) {
            setEditedParameter(cloneDeep(model));
        } else {
            setEditedParameter(null);
        }
    }

    function handleSave() {
        unset(editedParameter, 'spec.type');
        dispatch({type: 'update-parameter-def', parameter: editedParameter});
        const fragments = location.pathname.split('/');
        fragments.splice(-1, 1, snakeCase(editedParameter.spec.name));
        const newPath = fragments.join('/');
        history.replace({pathname: newPath});
    }

    return model ? (
        <Fragment>
            <Row>
                <Col xs={24}>
                    <div className="breadcrumbContainer">
                        <Breadcrumb separator=">">
                            {app.state.breadcrumb.length > 1 &&
                                app.state.breadcrumb.map((entry) => (
                                    <Breadcrumb.Item key={entry.url}>
                                        <Tooltip title={`Jump to ${entry.category}/${entry.key}`}>
                                            <Link to={entry.url}>
                                                {entry.title}[{entry.version}]
                                            </Link>
                                        </Tooltip>
                                    </Breadcrumb.Item>
                                ))}
                        </Breadcrumb>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col xs={24}>
                    <Tabs
                        type="card"
                        tabPlacement="right"
                        id="modelTabs"
                        items={[
                            {
                                key: 'visual',
                                label: <EyeOutlined />,
                                children: (
                                    <div className="scrollableViewer">
                                        <Block noEdit={!canEdit()} canSave={isValid} onEdit={triggerEdit} onSave={handleSave} mobile={mobile}>
                                            <ComponentName model={model} />
                                            {!editMode ? (
                                                <Fragment>
                                                    <MarkdownView source={model.spec.description} />
                                                    <Descriptions bordered column={{xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1}}>
                                                        <Descriptions.Item label="Name">{model.spec.name}</Descriptions.Item>
                                                        <Descriptions.Item label="Type">{get(model.spec, 'schema.type')}</Descriptions.Item>
                                                        <Descriptions.Item label="In">{model.spec.in}</Descriptions.Item>
                                                        <Descriptions.Item label="Required">{model.spec.required ? <Tag color="green">yes</Tag> : <Tag color="#ccc">no</Tag>}</Descriptions.Item>
                                                        <Descriptions.Item label="Deprecated">{model.spec.deprecated ? <Tag color="green">yes</Tag> : <Tag color="#ccc">no</Tag>}</Descriptions.Item>
                                                        <Descriptions.Item label="Allow Empty Value">
                                                            {model.spec.allowEmptyValue ? <Tag color="green">yes</Tag> : <Tag color="#ccc">no</Tag>}
                                                        </Descriptions.Item>
                                                    </Descriptions>
                                                </Fragment>
                                            ) : (
                                                <ParameterEditor parameter={editedParameter} setIsValid={setIsValid} />
                                            )}
                                        </Block>
                                    </div>
                                ),
                            },
                            {
                                key: 'technical',
                                label: <CodeOutlined />,
                                disabled: state.editMode,
                                children: (
                                    <>
                                        <ComponentName model={model} />
                                        <Block noEdit>
                                            <TechnicalEditor object={model.spec} update={onTechnicalEdit} />
                                        </Block>
                                    </>
                                ),
                            },
                            {
                                key: 'lint',
                                label: errorCount > 0 ? (
                                    <Badge count={errorCount}>
                                        <MacCommandOutlined />
                                    </Badge>
                                ) : (
                                    <MacCommandOutlined />
                                ),
                                disabled: state.editMode,
                                children: (
                                    <Block noEdit>
                                        <LintView type="models" componentType="parameters" modelKey={model.name} />
                                    </Block>
                                ),
                            },
                        ]}
                    />
                </Col>
            </Row>
        </Fragment>
    ) : null;
};
